import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import PublicLayout from "@/components/PublicLayout";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { useSiteContent } from "@/context/SiteContentContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Phone, Mail, MapPin, Loader2, Send, CheckCircle2 } from "lucide-react";

export default function ContactPage() {
  const { content } = useSiteContent();
  const ct = content.contatti;
  const g = content.general;
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Messaggio inviato! Ti risponderemo al più presto.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const recapiti = [
    { icon: Phone, t: ct.recapiti_phone_label || "Telefono", d: g.phone },
    { icon: Mail, t: ct.recapiti_email_label || "Email", d: g.email },
    { icon: MapPin, t: ct.recapiti_address_label || "Sede", d: g.address },
  ];

  return (
    <PublicLayout>
      <section className="pt-32 pb-16 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-bold uppercase tracking-widest text-teal-400">{ct.hero_kicker}</p>
          <h1 className="font-heading text-5xl font-extrabold mt-2">{ct.hero_title}</h1>
          <p className="mt-4 text-slate-300 max-w-2xl text-lg">{ct.hero_subtitle}</p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-5 gap-12">
          <div className="md:col-span-2 space-y-6">
            <h2 className="font-heading text-2xl font-bold text-white">{ct.recapiti_title}</h2>
            {recapiti.map((c, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-xl bg-teal-500/15 flex items-center justify-center shrink-0"><c.icon className="h-5 w-5 text-teal-600" /></div>
                <div><p className="font-semibold text-slate-100">{c.t}</p><p className="text-slate-400">{c.d}</p></div>
              </div>
            ))}
          </div>

          <div className="md:col-span-3">
            {sent ? (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="rounded-2xl border border-teal-200 bg-teal-50 p-10 text-center" data-testid="contact-success">
                <CheckCircle2 className="h-14 w-14 text-teal-600 mx-auto" />
                <h3 className="font-heading text-2xl font-semibold text-white mt-4">{ct.success_title || "Grazie!"}</h3>
                <p className="text-slate-300 mt-2">{ct.success_text || "Abbiamo ricevuto il tuo messaggio e ti ricontatteremo al più presto."}</p>
                <Button className="mt-6 bg-teal-600 hover:bg-teal-700" onClick={() => setSent(false)}>{ct.success_button || "Invia un altro messaggio"}</Button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="rounded-2xl bg-slate-900/70 border border-white/10 p-8 shadow-sm space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2"><Label>{ct.form_name_label || "Nome e cognome"}</Label><Input data-testid="contact-name" value={form.name} onChange={update("name")} required placeholder={ct.form_name_ph || "Mario Rossi"} /></div>
                  <div className="space-y-2"><Label>{ct.form_phone_label || "Telefono"}</Label><Input data-testid="contact-phone" value={form.phone} onChange={update("phone")} placeholder={ct.form_phone_ph || "+39 ..."} /></div>
                </div>
                <div className="space-y-2"><Label>{ct.form_email_label || "Email"}</Label><Input type="email" data-testid="contact-email" value={form.email} onChange={update("email")} required placeholder={ct.form_email_ph || "nome@studio.it"} /></div>
                <div className="space-y-2"><Label>{ct.form_message_label || "Messaggio"}</Label><Textarea data-testid="contact-message" value={form.message} onChange={update("message")} required rows={5} placeholder={ct.form_message_ph || "Descrivi la tua richiesta..."} /></div>
                <Button type="submit" data-testid="contact-submit" disabled={loading} className="bg-teal-600 hover:bg-teal-700 h-12 px-8">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />} {ct.form_submit || "Invia richiesta"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
